/**
 * TemplateDetailPage — 邮件模板详情
 * 展示模板完整信息（含附件），并提供编辑 / 预览 / 审核（提交·通过·驳回）/ 删除 / 附件 操作。
 * 列表行不再内联这些操作，统一收敛到本详情页。
 */
import { useState, useRef } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Spinner,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Field,
  Textarea,
} from '@fluentui/react-components'
import {
  EditRegular,
  EyeRegular,
  SendRegular,
  CheckRegular,
  DismissRegular,
  DeleteRegular,
  AttachRegular,
} from '@fluentui/react-icons'
import { templatesApi } from '@/api/templates'
import type { Template, Attachment } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { ROUTES } from '@/routes'
import DetailLayout, { DetailSection, DetailRow } from '@/components/DetailLayout'
import DangerButton from '@/components/DangerButton'
import { useDetailItem } from '@/hooks/useDetailItem'
import { formatDate } from '@/lib/format'

const STATUS: Record<string, { color: 'subtle' | 'brand' | 'success' | 'danger'; label: string }> = {
  draft: { color: 'subtle', label: '草稿' },
  pending: { color: 'brand', label: '待审核' },
  approved: { color: 'success', label: '已通过' },
  rejected: { color: 'danger', label: '已驳回' },
}

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  dialogActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '8px' },
  wideSurface: { width: '720px', maxWidth: '92vw' },
  previewSurface: { width: '760px', maxWidth: '94vw' },
  previewFrame: { width: '100%', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, minHeight: '380px' },
  attRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0' },
  deleteName: { fontWeight: 600, color: tokens.colorStatusDangerForeground1 },
})

export default function TemplateDetailPage() {
  const styles = useStyles()
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)

  // 列表缓存中的记录（即时展示）
  const { item: listItem, isLoading: listLoading } = useDetailItem<Template>(
    ['templates'],
    () => templatesApi.list(),
    id as string,
    'id',
  )
  // 行点击带入的瞬时记录
  const stateItem = (location.state as { item?: Template } | null)?.item
  // 权威完整记录（含附件）
  const templateQuery = useQuery({
    queryKey: ['template', id],
    queryFn: () => templatesApi.get(Number(id)),
  })
  const full = templateQuery.data

  const display = full ?? stateItem ?? listItem
  const loading = listLoading && !stateItem && !full

  // ── 编辑对话框 ──
  const [editorOpen, setEditorOpen] = useState(false)
  const [form, setForm] = useState({ name: '', subject: '', body_html: '', body_text: '', variables: '[]' })

  const openEdit = () => {
    const t = display
    if (!t) return
    setForm({
      name: t.name,
      subject: t.subject,
      body_html: t.body_html ?? '',
      body_text: t.body_text ?? '',
      variables: typeof t.variables === 'string' ? t.variables : JSON.stringify(t.variables ?? []),
    })
    setEditorOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      let vars: unknown = []
      try { vars = JSON.parse(form.variables || '[]') } catch { vars = [] }
      const payload = {
        name: form.name.trim(),
        subject: form.subject.trim(),
        body_html: form.body_html || null,
        body_text: form.body_text || null,
        variables: vars,
      }
      return templatesApi.update(Number(id), payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      templateQuery.refetch()
      toast.success('模板已更新（状态重置为草稿）')
      setEditorOpen(false)
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '保存失败'),
  })

  // ── 预览对话框 ──
  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null)
  const handlePreview = () => {
    const t = full ?? display
    if (!t) return
    setPreview({ subject: t.subject, html: t.body_html || `<pre>${t.body_text || ''}</pre>` })
  }

  // ── 审核（提交 / 通过 / 驳回）──
  const reviewMutation = useMutation({
    mutationFn: (action: 'submit' | 'approve' | 'reject') => templatesApi.review(Number(id), action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      templateQuery.refetch()
      toast.success('操作成功')
    },
    onError: (err) => toast.error('操作失败', err instanceof ApiError ? err.message : '操作失败'),
  })

  // ── 删除 ──
  const [deleteOpen, setDeleteOpen] = useState(false)
  const deleteMutation = useMutation({
    mutationFn: () => templatesApi.remove(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      toast.success('删除成功')
      navigate(ROUTES.TEMPLATES)
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '删除失败'),
  })

  // ── 附件 ──
  const deleteAttMutation = useMutation({
    mutationFn: (attId: number) => templatesApi.deleteAttachment(attId),
    onSuccess: () => {
      templateQuery.refetch()
      toast.success('附件已删除')
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '删除失败'),
  })

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    templatesApi.uploadAttachment(Number(id), file)
      .then(() => { templateQuery.refetch(); toast.success('附件已上传') })
      .catch((err) => toast.error('上传失败', err instanceof ApiError ? err.message : '上传失败'))
  }

  return (
    <>
      <DetailLayout
        title={display?.name ?? '邮件模板'}
        subtitle={display?.subject}
        badge={
          <Badge appearance="filled" color={STATUS[display?.status ?? '']?.color ?? 'subtle'}>
            {STATUS[display?.status ?? '']?.label ?? display?.status}
          </Badge>
        }
        media={undefined}
        loading={loading}
        notFound={!loading && !display}
        notFoundText="未找到该模板"
        actions={
          <>
            <Button appearance="subtle" icon={<EditRegular />} onClick={openEdit}>编辑</Button>
            <Button appearance="subtle" icon={<EyeRegular />} onClick={handlePreview}>预览</Button>
            {(display?.status === 'draft' || display?.status === 'rejected') && (
              <Button appearance="subtle" icon={<SendRegular />} onClick={() => reviewMutation.mutate('submit')}>提交审核</Button>
            )}
            {display?.status === 'pending' && (
              <>
                <Button appearance="subtle" icon={<CheckRegular />} onClick={() => reviewMutation.mutate('approve')}>通过</Button>
                <Button appearance="subtle" icon={<DismissRegular />} onClick={() => reviewMutation.mutate('reject')}>驳回</Button>
              </>
            )}
            <Button appearance="subtle" icon={<DeleteRegular />} onClick={() => setDeleteOpen(true)}>删除</Button>
          </>
        }
      >
        <DetailSection title="模板信息">
          <DetailRow label="ID" value={display?.id} />
          <DetailRow label="名称" value={display?.name} />
          <DetailRow label="主题" value={display?.subject} />
          <DetailRow label="变量定义" value={typeof display?.variables === 'string' ? display.variables : JSON.stringify(display?.variables ?? [])} full />
          <DetailRow label="HTML 正文" value={display?.body_html} full />
          <DetailRow label="纯文本正文" value={display?.body_text} full />
          <DetailRow label="状态" value={display?.status} />
          <DetailRow label="更新时间" value={formatDate(display?.updated_at)} />
        </DetailSection>

        <DetailSection title="附件">
          {(display?.attachments ?? []).map((att: Attachment) => (
            <div key={att.id} className={styles.attRow}>
              <AttachRegular fontSize={16} />
              <span>{att.filename}</span>
              <Text style={{ color: tokens.colorNeutralForeground3 }}>{(att.size / 1024).toFixed(1)} KB</Text>
              <Button appearance="subtle" size="small" icon={<DeleteRegular />} onClick={() => deleteAttMutation.mutate(att.id)}>删除</Button>
            </div>
          ))}
          <Button icon={<AttachRegular />} onClick={() => fileRef.current?.click()}>上传附件</Button>
          <input ref={fileRef} type="file" hidden onChange={onFilePicked} />
        </DetailSection>
      </DetailLayout>

      {/* 编辑对话框 */}
      <Dialog open={editorOpen} onOpenChange={(_, d) => { if (!d.open) setEditorOpen(false) }}>
        <DialogSurface className={styles.wideSurface}>
          <DialogBody>
            <DialogTitle>编辑模板</DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                <Field label="模板名称" required>
                  <Input value={form.name} onChange={(_, d) => setForm((f) => ({ ...f, name: d.value }))} placeholder="例如：验证码邮件、订单通知" />
                </Field>
                <Field label="主题（支持 {{变量}}）" required>
                  <Input value={form.subject} onChange={(_, d) => setForm((f) => ({ ...f, subject: d.value }))} placeholder="例如：您的验证码是 {{code}}" />
                </Field>
                <Field label="HTML 正文（支持 {{变量}}）">
                  <Textarea rows={8} value={form.body_html} onChange={(_, d) => setForm((f) => ({ ...f, body_html: d.value }))} placeholder="<p>您好 {{name}}，您的验证码是 {{code}}</p>" style={{ fontFamily: 'monospace' }} />
                </Field>
                <Field label="纯文本正文（备选）">
                  <Textarea rows={3} value={form.body_text} onChange={(_, d) => setForm((f) => ({ ...f, body_text: d.value }))} placeholder="您好 {{name}}，您的验证码是 {{code}}" />
                </Field>
                <Field label="变量定义（JSON 数组，仅用于说明）">
                  <Textarea rows={3} value={form.variables} onChange={(_, d) => setForm((f) => ({ ...f, variables: d.value }))} placeholder='[{"name":"code","desc":"验证码"}]' style={{ fontFamily: 'monospace' }} />
                </Field>
              </div>
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button onClick={() => setEditorOpen(false)}>取消</Button>
              <Button appearance="primary" disabled={saveMutation.isPending || !form.name.trim() || !form.subject.trim()} icon={saveMutation.isPending ? <Spinner size="tiny" /> : undefined} onClick={() => saveMutation.mutate()}>保存</Button>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 预览对话框 */}
      <Dialog open={preview !== null} onOpenChange={(_, d) => { if (!d.open) setPreview(null) }}>
        <DialogSurface className={styles.previewSurface}>
          <DialogBody>
            <DialogTitle>预览：{preview?.subject}</DialogTitle>
            <DialogContent>
              {preview && (
                <iframe title="preview" sandbox="" srcDoc={preview.html} className={styles.previewFrame} />
              )}
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button appearance="primary" onClick={() => setPreview(null)}>关闭</Button>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除确认 */}
      <Dialog open={deleteOpen} onOpenChange={(_, d) => { if (!d.open) setDeleteOpen(false) }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除</DialogTitle>
            <DialogContent>
              <Text>
                确定要删除模板 <span className={styles.deleteName}>{display?.name}</span> 吗？此操作不可撤销。
              </Text>
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button onClick={() => setDeleteOpen(false)}>取消</Button>
              <DangerButton loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
                确认删除
              </DangerButton>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
